import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  IconArrowLeft,
  IconUpload,
  IconDownload,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconFileSpreadsheet,
  IconInfoCircle,
} from "@tabler/icons-react";
import {
  uploadBulkMenuItems,
  exportAllMenuItems,
  useMenuItems,
} from "../controllers/menu_item.controller";

const normalizeRow = (row) => {
  const getField = (...keys) => {
    for (const k of keys) {
      if (row[k] !== undefined && row[k] !== null) return row[k];
      const match = Object.keys(row).find(
        (key) => key.trim().toLowerCase() === k.trim().toLowerCase()
      );
      if (match && row[match] !== undefined && row[match] !== null) {
        return row[match];
      }
    }
    return "";
  };

  const title = String(getField("title", "Title", "menu_title", "name", "Item Name")).trim();
  const category = String(getField("category", "Category", "category_title", "Category Name")).trim();
  const description = String(getField("description", "Description", "desc", "Item Description")).trim();
  const price = getField("price", "Price", "Base Price", "Item Price");
  const net_price = getField("net_price", "Net Price", "netPrice", "net price");
  const tax = String(getField("tax", "Tax", "tax_title", "Tax Title", "tax_id")).trim();
  const printer = String(getField("printer", "Printer", "Kitchen Printer")).trim();
  const variants = String(getField("variants", "Variants", "variant", "Variant")).trim();
  const addons = String(getField("addons", "Addons", "addon", "Addon")).trim();
  const status = String(getField("status", "Status", "is_enabled", "Enabled", "Active")).trim();

  return {
    title,
    category,
    description,
    price,
    net_price,
    tax,
    printer,
    variants,
    addons,
    status: status || "Enabled",
  };
};

const MenuItemsImportPage = () => {
  const navigate = useNavigate();
  const [itemsData, setItemsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileSelected, setFileSelected] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const { data: menuItems } = useMenuItems();

  const processParsedRows = (rawRows) => {
    const normalized = rawRows
      .map(normalizeRow)
      .filter((r) => r.title && r.title.length > 0);

    if (normalized.length === 0) {
      toast.error("No valid menu item rows found in file. Make sure 'Title' column is populated.");
      return;
    }

    setItemsData(normalized);
    toast.success(`Loaded ${normalized.length} menu items from file!`);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileSelected(file.name);
    const fileName = file.name.toLowerCase();

    if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls")) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
          processParsedRows(json);
        } catch (err) {
          console.error("Excel parse error:", err);
          toast.error("Failed to read Excel file. Please ensure it is a valid .xlsx or .xls file.");
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          processParsedRows(results.data);
        },
        error: (err) => {
          console.error("CSV parse error:", err);
          toast.error("Error parsing CSV file");
        },
      });
    }
  };

  const handleDownloadCurrent = async (format = "excel") => {
    try {
      setIsExporting(true);
      toast.loading("Exporting tenant menu items...");
      const res = await exportAllMenuItems();
      toast.dismiss();

      const items = res?.data || [];
      const dateStr = new Date().toISOString().slice(0, 10);

      const rows =
        items.length > 0
          ? items.map((i) => ({
              Title: i.title || "",
              Category: i.category || "General",
              Description: i.description || "",
              Price: i.price !== null && i.price !== undefined ? i.price : 0,
              "Net Price": i.net_price || "",
              Tax: i.tax || "",
              Printer: i.printer || "",
              Variants: i.variants || "",
              Addons: i.addons || "",
              Status: i.status || "Enabled",
            }))
          : [
              {
                Title: "Chicken Burger",
                Category: "Burgers",
                Description: "Juicy crispy chicken patty with lettuce and mayo",
                Price: 250,
                "Net Price": 221.24,
                Tax: "VAT 13%",
                Printer: "Kitchen 1",
                Variants: "Half: 150 | Full: 280",
                Addons: "Extra Cheese: 50 | Mayo: 30",
                Status: "Enabled",
              },
            ];

      if (format === "excel") {
        const worksheet = XLSX.utils.json_to_sheet(rows);
        worksheet["!cols"] = [
          { wch: 25 },
          { wch: 18 },
          { wch: 40 },
          { wch: 10 },
          { wch: 12 },
          { wch: 15 },
          { wch: 18 },
          { wch: 30 },
          { wch: 30 },
          { wch: 12 },
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Menu Items");
        XLSX.writeFile(workbook, `menu_items_${dateStr}.xlsx`);
        toast.success(`Exported ${rows.length} items to Excel!`);
      } else {
        const csv = Papa.unparse(rows);
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `menu_items_${dateStr}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Exported ${rows.length} items to CSV!`);
      }
    } catch (err) {
      console.error(err);
      toast.dismiss();
      toast.error("Failed to download menu items");
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadBlankTemplate = (format = "excel") => {
    const templateRows = [
      {
        Title: "Chicken Burger",
        Category: "Burgers",
        Description: "Juicy grilled chicken patty with fresh lettuce and sauce",
        Price: 250,
        "Net Price": 221.24,
        Tax: "VAT 13%",
        Printer: "Kitchen 1",
        Variants: "Half: 150 | Full: 280",
        Addons: "Extra Cheese: 50 | Mayo: 30",
        Status: "Enabled",
      },
      {
        Title: "Margherita Pizza",
        Category: "Pizza",
        Description: "Traditional mozzarella, basil, and tomato sauce",
        Price: 450,
        "Net Price": 398.23,
        Tax: "VAT 13%",
        Printer: "Pizza Oven",
        Variants: "Small: 300 | Medium: 450 | Large: 600",
        Addons: "Extra Cheese: 80 | Olives: 40",
        Status: "Enabled",
      },
      {
        Title: "Cold Coffee",
        Category: "Beverages",
        Description: "Rich espresso blended with chilled milk and ice cream",
        Price: 160,
        "Net Price": 141.59,
        Tax: "None",
        Printer: "Bar",
        Variants: "",
        Addons: "Whipped Cream: 40",
        Status: "Enabled",
      },
    ];

    if (format === "excel") {
      const worksheet = XLSX.utils.json_to_sheet(templateRows);
      worksheet["!cols"] = [
        { wch: 25 },
        { wch: 18 },
        { wch: 40 },
        { wch: 10 },
        { wch: 12 },
        { wch: 15 },
        { wch: 18 },
        { wch: 30 },
        { wch: 30 },
        { wch: 12 },
      ];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
      XLSX.writeFile(workbook, "menu_items_template.xlsx");
      toast.success("Downloaded Excel template!");
    } else {
      const csv = Papa.unparse(templateRows);
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "menu_items_template.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Downloaded CSV template!");
    }
  };

  const handleImport = async () => {
    if (itemsData.length === 0) {
      toast.error("No data to import");
      return;
    }

    setLoading(true);
    try {
      await uploadBulkMenuItems(itemsData);
      toast.success("Menu items imported and updated successfully!");
      navigate("/dashboard/settings/menu-items");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to import menu items");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/dashboard/settings/menu-items"
            className="p-2 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-full transition-colors text-gray-700 dark:text-gray-200"
          >
            <IconArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Bulk Import Menu Items
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Download existing tenant menu items, modify descriptions and details, and re-upload seamlessly.
            </p>
          </div>
        </div>

        {/* Download Actions Dropdown */}
        <div className="flex items-center gap-2">
          <div className="dropdown dropdown-end">
            <label
              tabIndex={0}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-700 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors text-sm font-medium cursor-pointer text-gray-700 dark:text-gray-200 shadow-sm"
            >
              <IconDownload className="w-4 h-4 text-emerald-600" />
              <span>{isExporting ? "Exporting..." : "Download Options"}</span>
            </label>
            <ul
              tabIndex={0}
              className="dropdown-content z-20 menu p-2 shadow-xl bg-white dark:bg-neutral-800 rounded-xl w-72 border border-gray-100 dark:border-neutral-700 mt-1"
            >
              <li className="menu-title text-xs font-semibold uppercase text-gray-400 px-3 py-1">
                Current Database Items
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => handleDownloadCurrent("excel")}
                  disabled={isExporting}
                  className="flex items-center gap-2 text-xs py-2 hover:bg-emerald-50 dark:hover:bg-neutral-700 text-emerald-700 dark:text-emerald-400 font-medium rounded-lg"
                >
                  <IconFileSpreadsheet size={16} />
                  Download Current Items (Excel .xlsx)
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => handleDownloadCurrent("csv")}
                  disabled={isExporting}
                  className="flex items-center gap-2 text-xs py-2 hover:bg-blue-50 dark:hover:bg-neutral-700 text-blue-700 dark:text-blue-400 font-medium rounded-lg"
                >
                  <IconDownload size={16} />
                  Download Current Items (CSV .csv)
                </button>
              </li>
              <div className="divider my-1"></div>
              <li className="menu-title text-xs font-semibold uppercase text-gray-400 px-3 py-1">
                Blank Sample Templates
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => handleDownloadBlankTemplate("excel")}
                  className="flex items-center gap-2 text-xs py-2 hover:bg-gray-100 dark:hover:bg-neutral-700 text-gray-700 dark:text-gray-200 rounded-lg"
                >
                  <IconFileSpreadsheet size={16} />
                  Blank Template (Excel .xlsx)
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => handleDownloadBlankTemplate("csv")}
                  className="flex items-center gap-2 text-xs py-2 hover:bg-gray-100 dark:hover:bg-neutral-700 text-gray-700 dark:text-gray-200 rounded-lg"
                >
                  <IconDownload size={16} />
                  Blank Template (CSV .csv)
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Upload Zone Card */}
      <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm border border-gray-200 dark:border-neutral-800 overflow-hidden">
        <div className="p-8 flex flex-col items-center justify-center border-b border-gray-100 dark:border-neutral-800">
          <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center mb-4">
            <IconUpload className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            Upload Menu File (.xlsx, .xls, .csv)
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center max-w-md">
            Upload your Excel spreadsheet or CSV file. All details (descriptions, variants, addons, printers) will be processed. Missing categories will be auto-created!
          </p>

          <label className="relative cursor-pointer">
            <input
              type="file"
              accept=".csv, .xlsx, .xls"
              onChange={handleFileChange}
              className="hidden"
            />
            <span className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-sm transition-all active:scale-95 font-medium flex items-center gap-2">
              <IconFileSpreadsheet className="w-5 h-5" />
              {fileSelected || "Select File (.xlsx or .csv)"}
            </span>
          </label>
        </div>

        {/* Data Preview */}
        {itemsData.length > 0 && (
          <div className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white text-base">
                  File Preview ({itemsData.length} items ready to import)
                </h4>
                <p className="text-xs text-gray-500">
                  Existing items will be updated with new prices and descriptions; new items will be inserted.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setItemsData([]);
                    setFileSelected(null);
                  }}
                  className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-neutral-800 rounded-lg transition"
                >
                  Clear
                </button>
                <button
                  onClick={handleImport}
                  disabled={loading}
                  className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition shadow active:scale-95 font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    "Processing Import..."
                  ) : (
                    <>
                      <IconCheck className="w-5 h-5" />
                      Confirm & Import {itemsData.length} Items
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-neutral-800 max-h-[500px]">
              <table className="w-full text-xs text-left text-gray-600 dark:text-gray-300">
                <thead className="text-[11px] uppercase bg-gray-50 dark:bg-neutral-800 text-gray-700 dark:text-gray-300 sticky top-0 z-10 font-bold border-b border-gray-200 dark:border-neutral-700">
                  <tr>
                    <th className="px-3 py-3">#</th>
                    <th className="px-3 py-3">Title</th>
                    <th className="px-3 py-3">Category</th>
                    <th className="px-3 py-3">Description</th>
                    <th className="px-3 py-3">Price</th>
                    <th className="px-3 py-3">Net Price</th>
                    <th className="px-3 py-3">Tax</th>
                    <th className="px-3 py-3">Printer</th>
                    <th className="px-3 py-3">Variants</th>
                    <th className="px-3 py-3">Addons</th>
                    <th className="px-3 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-neutral-800">
                  {itemsData.slice(0, 30).map((row, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50/70 dark:hover:bg-neutral-800/50 transition-colors"
                    >
                      <td className="px-3 py-2.5 text-gray-400 font-mono text-[10px]">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2.5 font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                        {row.title}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span className="px-2 py-0.5 rounded-full text-[10px] bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                          {row.category || "Uncategorized"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 max-w-[220px] truncate text-gray-500" title={row.description}>
                        {row.description ? row.description : <span className="text-gray-300 italic">None</span>}
                      </td>
                      <td className="px-3 py-2.5 font-medium whitespace-nowrap">
                        {row.price}
                      </td>
                      <td className="px-3 py-2.5 text-gray-400 whitespace-nowrap">
                        {row.net_price || "-"}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {row.tax || "-"}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-gray-500">
                        {row.printer || "-"}
                      </td>
                      <td className="px-3 py-2.5 max-w-[160px] truncate" title={row.variants}>
                        {row.variants ? (
                          <span className="text-purple-600 dark:text-purple-400">{row.variants}</span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 max-w-[160px] truncate" title={row.addons}>
                        {row.addons ? (
                          <span className="text-amber-600 dark:text-amber-400">{row.addons}</span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                            String(row.status).toLowerCase() === "disabled" ||
                            String(row.status) === "0"
                              ? "bg-gray-100 text-gray-600 dark:bg-neutral-800 dark:text-gray-400"
                              : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                          }`}
                        >
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {itemsData.length > 30 && (
              <p className="text-xs text-gray-400 mt-2 text-center">
                Showing first 30 of {itemsData.length} items. All items will be imported upon confirmation.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Guidance Cards */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/50 rounded-2xl p-5 text-emerald-900 dark:text-emerald-200">
          <div className="flex items-center gap-2 mb-2 font-semibold text-sm">
            <IconCheck className="w-5 h-5 text-emerald-600" />
            <span>Automatic Category & Smart Update</span>
          </div>
          <ul className="text-xs list-disc list-inside space-y-1.5 text-emerald-800/90 dark:text-emerald-300">
            <li>
              <strong>Automatic Category Creation:</strong> Any category not found in your database is automatically created for this tenant!
            </li>
            <li>
              <strong>Smart Upsert:</strong> Re-uploading an exported file will update existing items (preserving IDs) and insert new ones.
            </li>
            <li>
              <strong>Full Descriptions:</strong> Descriptions of any length are safely preserved and synchronized.
            </li>
          </ul>
        </div>

        <div className="bg-blue-50/60 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/50 rounded-2xl p-5 text-blue-900 dark:text-blue-200">
          <div className="flex items-center gap-2 mb-2 font-semibold text-sm">
            <IconInfoCircle className="w-5 h-5 text-blue-600" />
            <span>Variants, Addons & Printers Format</span>
          </div>
          <ul className="text-xs list-disc list-inside space-y-1.5 text-blue-800/90 dark:text-blue-300">
            <li>
              <strong>Variants format:</strong> Separate by pipe or comma: <code className="bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded">Half: 150 | Full: 280</code>
            </li>
            <li>
              <strong>Addons format:</strong> <code className="bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded">Extra Cheese: 50 | Mayo: 30</code>
            </li>
            <li>
              <strong>Printers:</strong> Specify kitchen printer name (e.g. <code className="bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded">Kitchen 1</code>).
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MenuItemsImportPage;
